/**
 * First-visit "Welcome" modal (site-wide).
 *
 * Content and behaviour come from Theme Settings › Welcome Modal (ACF options);
 * markup is rendered by template-parts/components/welcome-modal.php and only
 * exists when the feature is enabled.
 *
 * Consent (agreed with the privacy owner 2026-11-02):
 *   - The "already seen" record is Cookiebot category `preferences` (set in PHP
 *     by the `pacific_welcome_modal_consent` filter). It is localStorage key
 *     `pacific_welcome_modal:<campaign>`, value {"seen":<ms>,"exp":<ms>}, and
 *     is only read or written with that consent.
 *   - Retention: it expires after "Show again after" days, or after `maxDays`
 *     (365) when that setting is 0 ("show only once"). Expired records are
 *     deleted when found.
 *   - Without consent nothing is read or written, and the modal shows at most
 *     once per visit: only on the page the visitor enters the site through
 *     (external or empty referrer; never on a reload or back/forward).
 *   - Declining or withdrawing consent deletes every pacific_welcome_modal:*
 *     record, on that page and on any later page that loads this bundle.
 *     Consent given after the modal opened on this page records the visit then.
 *   - The modal waits until consent is known so it never opens on top of the
 *     consent banner. An unanswered banner means no modal on that page.
 *
 * Behaviour:
 *   - Shows once per browser per campaign key, and again after N days when
 *     "Show again after" is set (with consent; see above).
 *   - The visit is recorded on open, so navigating away without closing does
 *     not re-trigger it on the next page.
 *   - `?welcome=1` forces it open on any page and never reads or records the
 *     visit (QA / client review).
 *   - The CTA is a normal link; when it carries [data-video-modal] the click
 *     closes this modal and video-modal.js opens the player.
 *
 * Styles: assets/css/components/welcome-modal.css
 */

import { CATEGORIES, cmpStatus, hasConsent, onConsentChange, whenConsentKnown } from './cookie-consent.js';

const STORAGE_PREFIX = 'pacific_welcome_modal:';
const FOCUSABLE = 'a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"])';
const DAY_MS = 86400000;
// Fallbacks when the page has no modal config (feature off, Select site) and
// clean-up still runs. Keep in step with the PHP filter defaults.
const DEFAULT_CATEGORY = 'preferences';
const DEFAULT_MAX_DAYS = 365;

// ---------------------------------------------------------------------------
// Storage. Callers check consent first; every access tolerates blocked storage.
// ---------------------------------------------------------------------------

function storedKeys() {
    const keys = [];
    try {
        for (let i = 0; i < window.localStorage.length; i++) {
            const k = window.localStorage.key(i);
            if (k && k.indexOf(STORAGE_PREFIX) === 0) keys.push(k);
        }
    } catch (e) {
        /* storage blocked */
    }
    return keys;
}

function removeRecord(key) {
    try {
        window.localStorage.removeItem(key);
    } catch (e) {
        /* storage blocked */
    }
}

function removeAllRecords() {
    storedKeys().forEach(removeRecord);
}

function parseRecord(raw, maxDays) {
    if (!raw) return null;
    // Before 2026-11 the value was a bare Date.now() with no expiry.
    if (/^\d+$/.test(raw)) {
        const seen = Number(raw);
        return { seen, exp: seen + maxDays * DAY_MS };
    }
    try {
        const rec = JSON.parse(raw);
        if (rec && Number.isFinite(rec.seen) && Number.isFinite(rec.exp)) return rec;
    } catch (e) {
        /* corrupt — dropped below */
    }
    return null;
}

// Returns the live record, deleting it when expired or unreadable.
function readRecord(key, maxDays) {
    let raw = null;
    try {
        raw = window.localStorage.getItem(key);
    } catch (e) {
        return null;
    }
    const rec = parseRecord(raw, maxDays);
    if (raw && (!rec || rec.exp <= Date.now())) {
        removeRecord(key);
        return null;
    }
    return rec;
}

function removeExpiredRecords(maxDays) {
    storedKeys().forEach((k) => readRecord(k, maxDays));
}

function writeRecord(key, days, maxDays) {
    const now = Date.now();
    const keepDays = days > 0 ? Math.min(days, maxDays) : maxDays;
    try {
        window.localStorage.setItem(key, JSON.stringify({ seen: now, exp: now + keepDays * DAY_MS }));
    } catch (e) {
        /* private mode / blocked storage — nothing else to do */
    }
}

function shouldShowStored(key, days, maxDays) {
    const rec = readRecord(key, maxDays);
    if (!rec) return true;
    return days > 0 && Date.now() - rec.seen >= days * DAY_MS;
}

// No storage: is this the first page of the visit?
function isEntryPage() {
    try {
        const nav = window.performance.getEntriesByType('navigation')[0];
        if (nav && (nav.type === 'reload' || nav.type === 'back_forward')) return false;
    } catch (e) {
        /* Navigation Timing unavailable — fall back to the referrer */
    }
    if (!document.referrer) return true;
    try {
        return new URL(document.referrer).origin !== window.location.origin;
    } catch (e) {
        return true;
    }
}

export function init() {
    const forced = new URLSearchParams(window.location.search).get('welcome') === '1';
    if (!forced && window.self !== window.top) return; // never inside iframes / previews

    const root = document.getElementById('welcome-modal');

    let cfg = {};
    if (root) {
        try {
            cfg = JSON.parse(root.getAttribute('data-welcome-modal') || '{}');
        } catch (e) {
            cfg = {};
        }
    }

    const key = STORAGE_PREFIX + (cfg.campaign || 'default');
    const days = Number(cfg.days) || 0;
    const maxDays = Number(cfg.maxDays) > 0 ? Number(cfg.maxDays) : DEFAULT_MAX_DAYS;
    const category = CATEGORIES.indexOf(cfg.consent) !== -1 ? cfg.consent : DEFAULT_CATEGORY;
    const delayMs = Math.max(0, Number(cfg.delay) || 0) * 1000;
    const startedAt = Date.now();

    let settled = false; // consent known (or no CMP)
    let shownHere = false; // opened on this page view (not forced)

    // Keep stored records in line with consent on every page that loads this
    // bundle, whether or not the modal is enabled here.
    function syncStorage() {
        if (!settled || cmpStatus() === 'undecided') return;
        if (!hasConsent(category)) {
            removeAllRecords();
            return;
        }
        removeExpiredRecords(maxDays);
        if (root && shownHere && !readRecord(key, maxDays)) writeRecord(key, days, maxDays);
    }

    onConsentChange(syncStorage);

    if (!root) {
        whenConsentKnown(() => {
            settled = true;
            syncStorage();
        });
        return;
    }

    const dialog = root.querySelector('.welcome-modal__dialog');
    let lastFocus = null;
    let isOpen = false;

    function open() {
        if (isOpen) return;
        if (document.body.classList.contains('video-modal-open')) return;
        lastFocus = document.activeElement;
        root.hidden = false;
        document.body.classList.add('welcome-modal-open');
        requestAnimationFrame(() => root.classList.add('is-open'));
        isOpen = true;
        if (!forced) {
            shownHere = true;
            if (hasConsent(category)) writeRecord(key, days, maxDays);
        }
        if (dialog) dialog.focus();
    }

    function close() {
        if (!isOpen) return;
        isOpen = false;
        root.classList.remove('is-open');
        document.body.classList.remove('welcome-modal-open');
        root.hidden = true;
        if (lastFocus && document.contains(lastFocus) && typeof lastFocus.focus === 'function') {
            lastFocus.focus();
        }
        lastFocus = null;
    }

    function trap(e) {
        const items = [...root.querySelectorAll(FOCUSABLE)];
        if (!items.length) return;
        const first = items[0];
        const last = items[items.length - 1];
        if (e.shiftKey && (document.activeElement === first || document.activeElement === dialog)) {
            e.preventDefault();
            last.focus();
        } else if (!e.shiftKey && document.activeElement === last) {
            e.preventDefault();
            first.focus();
        }
    }

    root.addEventListener('click', (e) => {
        if (e.target.closest('[data-welcome-modal-close]')) {
            e.preventDefault();
            close();
            return;
        }
        // Plain-link CTA (video popup off): close before the browser navigates.
        if (e.target.closest('[data-welcome-modal-cta]')) {
            close();
        }
    });

    // Video CTA: video-modal.js claims the click in the capture phase and then
    // announces the open — step aside so the player is not stacked on top of us.
    document.addEventListener('pacific:video-modal-open', close);

    root.addEventListener('keydown', (e) => {
        if (e.key === 'Escape') {
            e.preventDefault();
            close();
        } else if (e.key === 'Tab') {
            trap(e);
        }
    });

    if (forced) {
        window.setTimeout(open, 0);
    }

    whenConsentKnown(() => {
        settled = true;
        syncStorage();
        if (forced) return;
        const show = hasConsent(category) ? shouldShowStored(key, days, maxDays) : isEntryPage();
        if (show) window.setTimeout(open, Math.max(0, delayMs - (Date.now() - startedAt)));
    });
}
